import React from 'react'
import _ from 'lodash'
import classnames from 'classnames/bind'
import type { Node } from '../../../types'
import Text from '../text'
import styles from '../jsonViewer/styles.scss'

const cx = classnames.bind(styles)

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export type JsonViewerClassNames = {
  container?: string
  viewer?: string
  key?: string
  value?: string
  valueText?: string
}

// ---------------------------------------------------------------------------
// Shared utilities
// ---------------------------------------------------------------------------

export const isArrayOfObjects = (data: Node[]): boolean =>
  data.length > 0 &&
  data.some(item => typeof item === 'object' && item !== null)

export const toProperCase = (str: string): string =>
  str.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())

// ---------------------------------------------------------------------------
// Read-only rendering primitives
// ---------------------------------------------------------------------------

/**
 * Renders a single key-value row where the value is a primitive (string, number, boolean, null).
 */
export const ReadOnlyValue: React.FC<{
  keyName: string
  value: Node
  classNames?: JsonViewerClassNames
}> = ({ keyName, value, classNames }) => (
  <div className={cx('json-value', classNames?.value)}>
    <Text size='tiny' className={cx('json-key', classNames?.key)}>
      {toProperCase(keyName)}:
    </Text>
    <Text
      size='tiny'
      className={cx('json-value-text', classNames?.valueText)}
      weight='heavy'
    >
      {JSON.stringify(value)}
    </Text>
  </div>
)

/**
 * Renders a key label followed by the value of a primitive array (not objects).
 */
export const ReadOnlyPrimitiveArray: React.FC<{
  keyName: string
  value: Node[]
  classNames?: JsonViewerClassNames
}> = ({ keyName, value, classNames }) => (
  <div className={cx('json-value', classNames?.value)}>
    <Text size='tiny' className={cx('json-key', classNames?.key)}>
      {toProperCase(keyName)}:
    </Text>
    <Text
      size='tiny'
      className={cx('json-value-text', classNames?.valueText)}
      weight='heavy'
    >
      {_.isEmpty(value) ? <i>(empty array)</i> : JSON.stringify(value)}
    </Text>
  </div>
)

/**
 * Renders an "(empty object)" indicator.
 */
export const ReadOnlyEmptyObject: React.FC<{
  classNames?: JsonViewerClassNames
}> = ({ classNames }) => (
  <div className={cx('json-value', classNames?.value)}>
    <Text
      size='tiny'
      className={cx('json-value-text', classNames?.valueText)}
      weight='heavy'
    >
      <i>(empty object)</i>
    </Text>
  </div>
)

/**
 * Renders a plain array of primitive values (no keys).
 */
export const PrimitiveArrayItems: React.FC<{
  data: Node[]
  classNames?: JsonViewerClassNames
}> = ({ data, classNames }) => (
  <div className={cx('json-array')}>
    {data.map((item: Node, index: number) => (
      <div key={index}>
        <Text
          size='tiny'
          className={cx('json-value-text', classNames?.valueText)}
          weight='heavy'
        >
          {JSON.stringify(item)}
        </Text>
      </div>
    ))}
  </div>
)

/**
 * Renders a key label for nested structures (objects / arrays of objects).
 */
export const NestedKeyLabel: React.FC<{
  keyName: string
  classNames?: JsonViewerClassNames
}> = ({ keyName, classNames }) => (
  <Text size='tiny' weight='medium' className={cx('json-key', classNames?.key)}>
    {toProperCase(keyName)}:
  </Text>
)

/**
 * Renders the index badge shown in top-right of array items.
 */
export const IndexBadge: React.FC<{ name: string }> = ({ name }) => (
  <Text size='tiny' color='white' className={cx('json-index')}>
    {toProperCase(name)}
  </Text>
)

/**
 * Returns the alternating background class name for a given nesting level.
 */
export const getBackgroundClass = (level: number): string =>
  level % 2 === 0 ? 'bg-even' : 'bg-odd'

/**
 * Binds classnames to jsonViewer styles for external consumers.
 */
export { cx }
